import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import Loading from "./Loading";
import Swal from "sweetalert2";
import "./AdminOrderDetail.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminOrderDetail = () => {
  const { authFetch } = useContext(AuthContext);
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const { setIsLoading } = useContext(LoadingContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [variantDetails, setVariantDetails] = useState({});

  const statusOptions = [
    "Paid",
    "Failed",
    "Order Processing",
    "Pending Payment",
    "Awaiting Delivery",
    "Awaiting Pickup",
    "Partially Shipped",
    "Out for Delivery",
    "Completed",
    "Cancelled",
    "Delayed",
    "Returned",
    "Refunded"
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setLoading(true);
      try {
        const orderResponse = await authFetch(
          `${API_BASE_URL}/api/orders/${orderId}`
        );
        if (!orderResponse.ok) {
          throw new Error("Failed to fetch order");
        }
        const orderData = await orderResponse.json();
        setOrder(orderData);
        setSelectedStatus(orderData.orderStatus);

        const items = orderData.orderItems || [];
        setOrderItems(items);
        const variantDetailsObj = {};
        for (const item of items) {
          if (item.variantsId && item.variantsId !== 0) {
            try {
              const variantResponse = await authFetch(
                `${API_BASE_URL}/products/variant/${item.variantsId}`
              );

              if (variantResponse.ok) {
                const variantData = await variantResponse.json();
                variantDetailsObj[item.variantsId] = variantData;
              }
            } catch (error) {
              // Silent fail (previously only 1 console.error line in this catch block, no other actions)
            }
          }
        }

        setVariantDetails(variantDetailsObj);
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [orderId]);



  const handlePrint = () => {
    const printFrame = document.createElement('iframe');

    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';

    document.body.appendChild(printFrame);

    const customerName = order.username || '';
    // When billing = shipping, the billing contact IS the shipping contact person/number.
    const billingName = order.isBillingSameAsShipping
      ? (order.shippingContactName || order.billingName || customerName)
      : (order.billingName || customerName);
    const billingPhone = order.isBillingSameAsShipping
      ? (order.shippingContactNumber || order.billingPhoneNumber || order.phoneNumber || '')
      : (order.billingPhoneNumber || order.phoneNumber || '');
    const billingAddr = order.isBillingSameAsShipping ? formatAddress() : formatBillingAddress();
    const shippingAddr = formatAddress();
    const shippingContactName = order.shippingContactName || customerName;
    const shippingContactNumber = order.shippingContactNumber || order.phoneNumber || '';
    const receiptDate = (() => {
      if (!order.orderDate) return 'N/A';
      const d = new Date(order.orderDate);
      return `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    })();

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order Receipt #${order.orderNum}</title>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #000;
            background-color: white;
            font-size: 12px;
            line-height: 1.4;
          }
          .receipt-container {
            max-width: 780px;
            margin: 0 auto;
            padding: 20px;
            background-color: white;
          }
          .header-section {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin-bottom: 5px;
          }
          .company-logo {
            width: 50px;
            height: 40px;
            object-fit: contain;
            flex-shrink: 0;
          }
          .company-info {
            flex: 1;
          }
          .company-name {
            font-size: 13px;
            font-weight: bold;
          }
          .company-reg {
            font-size: 13px;
            font-weight: bold;
          }
          .company-address {
            font-size: 10px;
            margin-top: 2px;
          }
          .receipt-header {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 15px;
          }
          .receipt-right {
            text-align: right;
          }
          .receipt-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .receipt-meta {
            font-size: 11px;
          }
          .receipt-meta .label {
            font-weight: bold;
          }
          .address-section {
            display: flex;
            gap: 40px;
            margin: 20px 0;
          }
          .address-block {
            flex: 1;
          }
          .address-title {
            font-size: 13px;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .address-name {
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 4px;
          }
          .address-text {
            font-size: 11px;
            line-height: 1.5;
          }
          .order-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0 10px 0;
          }
          .order-table th {
            background-color: #f5f5f5;
            border: 1px solid #000;
            padding: 6px 8px;
            font-size: 11px;
            font-weight: bold;
            text-align: center;
          }
          .order-table td {
            border: 1px solid #ccc;
            padding: 6px 8px;
            font-size: 11px;
            vertical-align: top;
          }
          .col-no { width: 5%; text-align: center; }
          .col-code { width: 18%; }
          .col-spec { width: 32%; }
          .col-qty { width: 10%; text-align: center; }
          .col-unit { width: 10%; text-align: center; }
          .col-price { width: 10%; text-align: right; }
          .col-total { width: 15%; text-align: right; }
          .totals-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0;
          }
          .totals-table td {
            padding: 4px 8px;
            font-size: 11px;
          }
          .totals-label {
            text-align: right;
            font-weight: bold;
            border: 1px solid #ccc;
          }
          .totals-value {
            text-align: right;
            width: 15%;
            border: 1px solid #ccc;
          }
          .grand-total .totals-label,
          .grand-total .totals-value {
            font-weight: bold;
            font-size: 12px;
          }
          .payment-delivery {
            margin-top: 20px;
            font-size: 12px;
          }
          .payment-delivery div {
            margin-bottom: 5px;
          }
          .payment-delivery .label {
            font-weight: bold;
          }
          .remarks-section {
            margin-top: 20px;
            border: 1px solid #000;
            padding: 12px 15px;
          }
          .remarks-title {
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 5px;
          }
          .remarks-text {
            font-size: 11px;
            white-space: pre-wrap;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          @page {
            margin: 0;
            size: auto;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
            }
            body { padding: 20px !important; }
            .receipt-container { border: none; padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <!-- Header -->
          <div class="header-section">
            <img src="/company-logo-2.png" alt="Logo" class="company-logo">
            <div class="company-info">
              <div class="company-name">ARDENCY CONSTRUCTION & TRADING SDN BHD <span class="company-reg">201701013881(1228046-W)</span></div>
              <div class="company-address">No.21, Jalan Wawasan 2C/KU 7 Sungai Kapar Indah Kapar, 42200 Klang, Selangor</div>
            </div>
          </div>

          <!-- Receipt Title & Meta -->
          <div class="receipt-header">
            <div class="receipt-right">
              <div class="receipt-title">ORDER RECEIPT</div>
              <div class="receipt-meta">
                <span class="label">RECEIPT NUMBER:</span> ${order.orderNum}<br>
                <span class="label">RECEIPT DATE:</span> ${receiptDate}
              </div>
            </div>
          </div>

          <!-- Billing & Delivery Address -->
          <div class="address-section">
            <div class="address-block">
              <div class="address-title">BILLING ADDRESS</div>
              <div class="address-name">${billingName}</div>
              <div class="address-text">
                ${billingAddr}<br><br>
                ATTN : ${billingName}<br>
                TEL : ${billingPhone}
              </div>
            </div>
            <div class="address-block">
              <div class="address-title">${order.isPickup ? 'SELF PICK UP' : 'DELIVERY ADDRESS'}</div>
              ${order.isPickup ? `
                <div class="address-text">Customer will self pick up at store.</div>
              ` : `
                <div class="address-name">${shippingContactName}</div>
                <div class="address-text">
                  ${shippingAddr}<br><br>
                  ATTN : ${shippingContactName}<br>
                  TEL : ${shippingContactNumber}
                </div>
              `}
            </div>
          </div>

          <!-- Order Items Table -->
          <table class="order-table">
            <thead>
              <tr>
                <th class="col-no">No.</th>
                <th class="col-code">Stock Code</th>
                <th class="col-spec">Specifications</th>
                <th class="col-qty">Qty</th>
                <th class="col-unit">Unit</th>
                <th class="col-price">Unit Price</th>
                <th class="col-total">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              ${orderItems.map((item, index) => {
      const specParts = [];
      if (item.variantDetails && item.variantDetails.length > 0) {
        specParts.push(item.variantDetails.map(v => v.specificationValue).join(', '));
      }
      const specText = specParts.length > 0 ? specParts.join(' - ') : getProductName(item);

      return `
                <tr>
                  <td class="col-no">${index + 1}</td>
                  <td class="col-code">${getProductCode(item)}</td>
                  <td class="col-spec">${getProductName(item)}${specParts.length > 0 ? '<br>' + specParts.join(', ') : ''}</td>
                  <td class="col-qty">${item.quantity}</td>
                  <td class="col-unit">${item.uom || '-'}</td>
                  <td class="col-price">${parseFloat(item.price).toFixed(2)}</td>
                  <td class="col-total">${calculateSubtotal(item.price, item.quantity)}</td>
                </tr>
              `;
    }).join('')}
            </tbody>
          </table>

          <!-- Totals -->
          <table class="totals-table">
            <tr>
              <td class="totals-label" colspan="6">SUBTOTAL (RM)</td>
              <td class="totals-value">${parseFloat(order.totalAmount).toFixed(2)}</td>
            </tr>
            <tr>
              <td class="totals-label" colspan="6">DELIVERY FEES</td>
              <td class="totals-value">${order.deliveryFees ? parseFloat(order.deliveryFees).toFixed(2) : ''}</td>
            </tr>
            <tr>
              <td class="totals-label" colspan="6">DISCOUNTS</td>
              <td class="totals-value">${order.discountAmount && parseFloat(order.discountAmount) > 0 ? parseFloat(order.discountAmount).toFixed(2) : ''}</td>
            </tr>
            <tr>
              <td class="totals-label" colspan="6">TAXES</td>
              <td class="totals-value">${order.taxes ? parseFloat(order.taxes).toFixed(2) : ''}</td>
            </tr>
            <tr class="grand-total">
              <td class="totals-label" colspan="6">GRAND TOTAL (RM)</td>
              <td class="totals-value">${parseFloat(order.finalAmount).toFixed(2)}</td>
            </tr>
          </table>

          <!-- Payment & Delivery Method -->
          <div class="payment-delivery">
            <div><span class="label">PAYMENT METHOD:</span> ${order.paymentMethod || '-'}</div>
            <div><span class="label">DELIVERY METHOD:</span> ${order.isPickup ? 'SELF PICK UP' : 'LORRY DELIVERY'}</div>
          </div>

          <!-- Remarks -->
          ${order.orderComment ? `
          <div class="remarks-section">
            <div class="remarks-title">REMARKS:</div>
            <div class="remarks-text">${order.orderComment}</div>
          </div>
          ` : ''}

          <div class="footer">
            Thank you for your order!
          </div>
        </div>
      </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow || printFrame.contentDocument.document || printFrame.contentDocument;
    frameDoc.document.open();
    frameDoc.document.write(receiptHTML);
    frameDoc.document.close();

    printFrame.onload = function () {
      try {
        frameDoc.focus();
        frameDoc.print();

        setTimeout(function () {
          document.body.removeChild(printFrame);
        }, 1000);
      } catch (e) {
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = receiptHTML;
        window.print();
        document.body.innerHTML = originalContent;
      }
    };
  };

  if (loading) {
    return (
      <div className="admin-order-detail-container">
        <div className="admin-order-detail-loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-order-detail-container">
        <div className="admin-order-detail-error">Error: {error}</div>
        <button
          className="admin-order-detail-back-btn"
          onClick={() => navigate("/admin/orders")}
        >
          <i className="fas fa-arrow-left"></i> Back to Orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="admin-order-detail-container">
        <div className="admin-order-detail-error">Order not found</div>
        <button
          className="admin-order-detail-back-btn"
          onClick={() => navigate("/admin/orders")}
        >
          <i className="fas fa-arrow-left"></i> Back to Orders
        </button>
      </div>
    );
  }

  // Get status class
  const getStatusClass = (status) => {
    if (!status) return "";

    const statusLower = status.toLowerCase().replace(/ /g, '-');
    switch (statusLower) {
      case "new":
        return "admin-order-detail-status-new";
      case "order-processing":
        return "admin-order-detail-status-order-processing";
      case "pending-payment":
        return "admin-order-detail-status-pending-payment";
      case "awaiting-delivery":
        return "admin-order-detail-status-awaiting-delivery";
      case "awaiting-pickup":
        return "admin-order-detail-status-awaiting-pickup";
      case "partially-shipped":
        return "admin-order-detail-status-partially-shipped";
      case "out-for-delivery":
        return "admin-order-detail-status-out-for-delivery";
      case "completed":
        return "admin-order-detail-status-completed";
      case "cancelled":
        return "admin-order-detail-status-cancelled";
      case "delayed":
        return "admin-order-detail-status-delayed";
      case "returned":
        return "admin-order-detail-status-returned";
      case "refunded":
        return "admin-order-detail-status-refunded";
      default:
        return "";
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const getProductName = (item) => {
    if (item.productName) {
      return item.productName;
    }
    if (item.variantsId && item.variantsId !== 0) {
      const details = variantDetails[item.variantsId];
      if (details && details.product) {
        return details.product.name;
      }
    }

    return "Unknown Product";
  };

  const getProductCode = (item) => {
    if (item.productCode) {
      return item.productCode;
    }
    if (item.variantsId && item.variantsId !== 0) {
      const details = variantDetails[item.variantsId];
      if (details && details.product) {
        return details.product.productCode || "N/A";
      }
    }

    return "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatAddress = () => {
    if (!order) return "";

    const addressParts = [];
    if (order.unitBlock) addressParts.push(order.unitBlock);
    if (order.shippingAddress1) addressParts.push(order.shippingAddress1);
    if (order.shippingAddress2) addressParts.push(order.shippingAddress2);

    let locationPart = "";
    if (order.shippingZipCode) locationPart += order.shippingZipCode;
    if (order.shippingCity) locationPart += " " + order.shippingCity;
    if (order.shippingState) locationPart += ", " + order.shippingState;
    if (locationPart) addressParts.push(locationPart.trim());

    return addressParts.join(", ");
  };

  const formatBillingAddress = () => {
    if (!order) return "";

    if (order.isBillingSameAsShipping) {
      return formatAddress();
    }

    const addressParts = [];
    if (order.billingUnitBlock) addressParts.push(order.billingUnitBlock);
    if (order.billingAddress1) addressParts.push(order.billingAddress1);
    if (order.billingAddress2) addressParts.push(order.billingAddress2);

    let locationPart = "";
    if (order.billingZipCode) locationPart += order.billingZipCode;
    if (order.billingCity) locationPart += " " + order.billingCity;
    if (order.billingState) locationPart += ", " + order.billingState;
    if (locationPart) addressParts.push(locationPart.trim());

    return addressParts.join(", ");
  };

  const calculateSubtotal = (price, quantity) => {
    return (parseFloat(price) * parseInt(quantity)).toFixed(2);
  };

  const handleUpdateStatus = async () => {
    if (selectedStatus === order.orderStatus) return;

    if (order.orderStatus.toLowerCase() === "refunded") {
      Swal.fire({
        title: "Cannot Update Status",
        text: "The status of a refunded order cannot be changed.",
        icon: "warning",
        confirmButtonColor: "#45a049",
      });
      return;
    }

    if (selectedStatus.toLowerCase() === "refunded") {
      Swal.fire({
        title: "Refund Order",
        text: `Are you sure you want to refund this order? This action will restore the stock quantities for all products in this order and cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#e74c3c",
        cancelButtonColor: "#7a7a7a",
        confirmButtonText: "Yes, Refund Order",
        cancelButtonText: "Cancel"
      }).then(async (result) => {
        if (result.isConfirmed) {
          await updateOrderStatus();
        }
      });
    } else {
      Swal.fire({
        title: "Change Order Status",
        text: `Are you sure you want to change the order status from ${order.orderStatus} to ${selectedStatus}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#45a049",
        cancelButtonColor: "#7a7a7a",
        confirmButtonText: "Yes",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await updateOrderStatus();
        }
      });
    }
  };

  const updateOrderStatus = async () => {
    setUpdatingStatus(true);
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/orders/${orderId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            OrderStatus: selectedStatus,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update order status");
      }

      setOrder({ ...order, orderStatus: selectedStatus });

      // Special success message for refund
      if (selectedStatus.toLowerCase() === "refunded") {
        Swal.fire({
          title: "Order Refunded!",
          text: "Order has been successfully refunded and stock quantities have been restored.",
          icon: "success",
          timer: 3000,
          confirmButtonColor: "#45a049",
        });
      } else {
        Swal.fire({
          title: "Success!",
          text: "Order status updated successfully!",
          icon: "success",
          timer: 2000,
          confirmButtonColor: "#45a049",
        });
      }
    } catch (error) {
      setError("Failed to update order status. Please try again.");

      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to update order status. Please try again.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="admin-order-detail-container">
      <div className="admin-order-detail-header">
        <button
          className="admin-order-detail-back-btn"
          onClick={() => navigate("/admin/orders")}
        >
          <i className="fas fa-arrow-left"></i> Back to Orders
        </button>
      </div>

      <div className="admin-order-detail-title">
        <h2>Order Details</h2>
        <div className="admin-order-detail-order-id">
          Order #{order.orderNum}
        </div>
      </div>
      <div className="admin-order-detail-content">
        {/* Order Information Card */}
        <div className="admin-order-detail-card">
          <h2 className="admin-order-detail-card-title">
            <i className="fas fa-info-circle"></i> Order Information
          </h2>
          <div className="admin-order-detail-info-grid">
            <div className="admin-order-detail-info-item">
              <span className="admin-order-detail-label">Order Status</span>
              <span
                className={`admin-order-detail-status ${getStatusClass(
                  order.orderStatus
                )}`}
              >
                {order.orderStatus}
              </span>
            </div>

            <div className="admin-order-detail-info-item">
              <span className="admin-order-detail-label">Order Date</span>
              <span className="admin-order-detail-value">
                {formatDate(order.orderDate)}
              </span>
            </div>

            <div className="admin-order-detail-info-item">
              <span className="admin-order-detail-label">Customer</span>
              <span className="admin-order-detail-value">
                {order.username || 'Guest'}
              </span>
            </div>

            <div className="admin-order-detail-info-item">
              <span className="admin-order-detail-label">Email</span>
              <span className="admin-order-detail-value">{order.email}</span>
            </div>

            <div className="admin-order-detail-info-item">
              <span className="admin-order-detail-label">Phone</span>
              <span className="admin-order-detail-value">
                {order.phoneNumber}
              </span>
            </div>

            <div className="admin-order-detail-info-item">
              <span className="admin-order-detail-label">Payment Method</span>
              <span className="admin-order-detail-value">
                {order.paymentMethod}
              </span>
            </div>

            <div className="admin-order-detail-info-item">
              <span className="admin-order-detail-label">Order Comment</span>
              <span className="admin-order-detail-value">
                {order.orderComment || "-"}
              </span>
            </div>
          </div>

          {/* E-Invoice */}
          <div
            className="admin-order-detail-card-title"
            style={{ marginTop: "20px" }}
          >
            <i className="fas fa-file-invoice"></i> E-Invoice
          </div>

          <div className="admin-order-detail-info-item">
            <span className="admin-order-detail-label">E-Invoice Requested</span>
            <span className="admin-order-detail-value">
              {order.eInvoiceRequested ? "Yes" : "No"}
            </span>
          </div>

          {order.eInvoiceRequested && (
            <>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">Buyer Type</span>
                <span className="admin-order-detail-value">
                  {order.eInvoiceBuyerType || "-"}
                </span>
              </div>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">
                  {order.eInvoiceBuyerType === "Company" ? "Company Name" : "Full Name"}
                </span>
                <span className="admin-order-detail-value">
                  {order.eInvoiceName || "-"}
                </span>
              </div>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">TIN</span>
                <span className="admin-order-detail-value">
                  {order.eInvoiceTIN || "-"}
                </span>
              </div>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">
                  {order.eInvoiceBuyerType === "Company" ? "Business Registration No." : "NRIC"}
                </span>
                <span className="admin-order-detail-value">
                  {order.eInvoiceBRN || "-"}
                </span>
              </div>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">SST Registration No.</span>
                <span className="admin-order-detail-value">
                  {order.eInvoiceSSTNumber || "-"}
                </span>
              </div>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">E-Invoice Email</span>
                <span className="admin-order-detail-value">
                  {order.eInvoiceEmail || "-"}
                </span>
              </div>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">E-Invoice Phone</span>
                <span className="admin-order-detail-value">
                  {order.eInvoicePhone || "-"}
                </span>
              </div>
              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">E-Invoice Address</span>
                <span className="admin-order-detail-value" style={{ whiteSpace: "pre-wrap" }}>
                  {order.eInvoiceAddress || "-"}
                </span>
              </div>
            </>
          )}

          <div
            className="admin-order-detail-card-title"
            style={{ marginTop: "20px" }}
          >
            <i className="fas fa-money-bill"></i> Price Summary
          </div>

          <div
            className="admin-order-detail-info-item"
            style={{ borderBottom: "1px solid #e1e4e8", paddingBottom: "10px" }}
          >
            <span className="admin-order-detail-label">Total Amount</span>
            <span className="admin-order-detail-value">
              RM{parseFloat(order.totalAmount).toFixed(2)}
            </span>
          </div>

          <div
            className="admin-order-detail-info-item"
            style={{
              backgroundColor: "rgb(250, 222, 195)",
              padding: "10px",
              borderRadius: "5px",
              marginTop: "10px",
              border: "1px dashed rgb(255, 150, 51)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                className="admin-order-detail-label"
                style={{ display: "flex", alignItems: "center", marginTop: "5px" }}
              >
                <i
                  className="fas fa-tag"
                  style={{ color: "rgb(179, 98, 22)", marginRight: "8px" }}
                ></i>
                Discount Applied
                {order.voucherCode && (
                  <span className="admin-order-detail-voucher-tag">
                    {order.voucherCode}
                  </span>
                )}
              </span>
              <span
                className="admin-order-detail-value"
                style={{ color: "rgb(179, 98, 22)", fontWeight: "bold" }}
              >
                - RM{parseFloat(order.discountAmount || 0).toFixed(2)}
              </span>
            </div>
          </div>

          <div
            className="admin-order-detail-info-item"
            style={{
              marginTop: "15px",
              backgroundColor: "#f5f5f5",
              padding: "15px",
              borderRadius: "5px",
            }}
          >
            <span
              className="admin-order-detail-label"
              style={{ fontSize: "16px", fontWeight: "600" }}
            >
              Final Amount
            </span>
            <span className="admin-order-detail-amount">
              RM{parseFloat(order.finalAmount).toFixed(2)}
            </span>
          </div>
          {/* Status Update Section */}
          {/* Status Update Section - Only show if order is not refunded */}
          {order.orderStatus.toLowerCase() !== "refunded" && (
            <div className="admin-order-detail-status-update">
              <div className="admin-order-detail-status-update-title">
                Update Order Status
              </div>

              <div className="admin-order-detail-status-options">
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    className={`admin-order-detail-status-option ${selectedStatus === status ? "active" : ""}`}
                    onClick={() => handleStatusChange(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>

              <button
                className="admin-order-detail-btn-action admin-order-detail-btn-primary"
                onClick={handleUpdateStatus}
                disabled={updatingStatus || selectedStatus === order.orderStatus}
                style={{
                  opacity: (updatingStatus || selectedStatus === order.orderStatus) ? 0.5 : 1,
                  cursor: (updatingStatus || selectedStatus === order.orderStatus) ? "not-allowed" : "pointer",
                  backgroundColor: selectedStatus.toLowerCase() === "refunded" && selectedStatus !== order.orderStatus ? "#e74c3c" : ""
                }}
              >
                {updatingStatus ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Updating...
                  </>
                ) : selectedStatus.toLowerCase() === "refunded" && selectedStatus !== order.orderStatus ? (
                  <>
                    <i className="fas fa-undo"></i> Refund Order
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i> Update Status
                  </>
                )}
              </button>

              {selectedStatus.toLowerCase() === "refunded" && selectedStatus !== order.orderStatus && (
                <div className="admin-order-detail-status-note" style={{
                  backgroundColor: "#fff3e0",
                  color: "#f57c00",
                  padding: "10px",
                  borderRadius: "4px",
                  margin: "10px 0",
                  border: "1px solid #ffcc02",
                  fontWeight: "500"
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: "8px" }}></i>
                  Warning: Refunding this order will restore stock quantities for all products and cannot be undone.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="admin-order-detail-card">
          {/* Shipping Information Card */}
          <h2 className="admin-order-detail-card-title">
            <i className="fas fa-shipping-fast"></i> Shipping Information
          </h2>

          {order.isPickup ? (
            <div className="admin-order-detail-info-item" style={{
              backgroundColor: '#e3f2fd',
              padding: '15px',
              borderRadius: '8px',
              border: '2px solid #2196f3',
              textAlign: 'center'
            }}>
              <i className="fas fa-store" style={{ marginRight: '8px', color: '#2196f3', fontSize: '1.2rem' }}></i>
              <strong style={{ color: '#1976d2', fontSize: '1rem' }}>
                This order will be collected at the store
              </strong>
            </div>
          ) : (
            <>
              <div className="admin-order-detail-info-grid">
                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">Contact Person Name</span>
                  <span className="admin-order-detail-value">
                    {order.shippingContactName || "-"}
                  </span>
                </div>

                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">Contact Number</span>
                  <span className="admin-order-detail-value">
                    {order.shippingContactNumber || "-"}
                  </span>
                </div>
              </div>

              <div className="admin-order-detail-info-item">
                <span className="admin-order-detail-label">Shipping Address</span>
                <div className="admin-order-detail-address admin-order-detail-value">
                  {formatAddress()}
                </div>
              </div>

              <div className="admin-order-detail-info-grid">
                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">Unit Block</span>
                  <span className="admin-order-detail-value">
                    {order.unitBlock || "-"}
                  </span>
                </div>

                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">Address Line 1</span>
                  <span className="admin-order-detail-value">
                    {order.shippingAddress1 || "-"}
                  </span>
                </div>

                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">Address Line 2</span>
                  <span className="admin-order-detail-value">
                    {order.shippingAddress2 || "-"}
                  </span>
                </div>

                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">Zip Code</span>
                  <span className="admin-order-detail-value">
                    {order.shippingZipCode || "-"}
                  </span>
                </div>

                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">City</span>
                  <span className="admin-order-detail-value">
                    {order.shippingCity || "-"}
                  </span>
                </div>

                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">State</span>
                  <span className="admin-order-detail-value">
                    {order.shippingState || "-"}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Billing Information Card */}
          <h2 className="admin-order-detail-card-title"
            style={{ marginTop: "20px" }}>
            <i className="fas fa-file-invoice-dollar"></i> Billing Information
          </h2>

          {(() => {
            const same = order.isBillingSameAsShipping;
            const bUnitBlock = same ? order.unitBlock : order.billingUnitBlock;
            const bAddr1 = same ? order.shippingAddress1 : order.billingAddress1;
            const bAddr2 = same ? order.shippingAddress2 : order.billingAddress2;
            const bZip = same ? order.shippingZipCode : order.billingZipCode;
            const bCity = same ? order.shippingCity : order.billingCity;
            const bState = same ? order.shippingState : order.billingState;
            // When billing = shipping, billing contact mirrors the shipping contact
            const bName = same ? (order.shippingContactName || order.billingName || order.username) : (order.billingName || order.username);
            const bPhone = same ? (order.shippingContactNumber || order.billingPhoneNumber || order.phoneNumber) : (order.billingPhoneNumber || order.phoneNumber);
            return (
              <>
                <div className="admin-order-detail-info-grid">
                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">Billing Name</span>
                    <span className="admin-order-detail-value">
                      {bName || "-"}
                    </span>
                  </div>
                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">Billing Phone</span>
                    <span className="admin-order-detail-value">
                      {bPhone || "-"}
                    </span>
                  </div>
                </div>

                <div className="admin-order-detail-info-item">
                  <span className="admin-order-detail-label">Billing Address</span>
                  <div className="admin-order-detail-address admin-order-detail-value">
                    {formatBillingAddress()}
                  </div>
                </div>

                <div className="admin-order-detail-info-grid">
                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">Unit Block</span>
                    <span className="admin-order-detail-value">{bUnitBlock || "-"}</span>
                  </div>

                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">Address Line 1</span>
                    <span className="admin-order-detail-value">{bAddr1 || "-"}</span>
                  </div>

                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">Address Line 2</span>
                    <span className="admin-order-detail-value">{bAddr2 || "-"}</span>
                  </div>

                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">Zip Code</span>
                    <span className="admin-order-detail-value">{bZip || "-"}</span>
                  </div>

                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">City</span>
                    <span className="admin-order-detail-value">{bCity || "-"}</span>
                  </div>

                  <div className="admin-order-detail-info-item">
                    <span className="admin-order-detail-label">State</span>
                    <span className="admin-order-detail-value">{bState || "-"}</span>
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        {/* Order Items Card */}
        <div className="admin-order-detail-card admin-order-detail-items-card">
          <h2 className="admin-order-detail-card-title">
            <i className="fas fa-shopping-cart"></i> Order Items
          </h2>

          <div className="admin-order-detail-table-container">
            <table className="admin-order-detail-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="admin-order-detail-quantity">Quantity</th>
                  <th className="admin-order-detail-price">Price</th>
                  <th className="admin-order-detail-subtotal">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.length > 0 ? (
                  orderItems.map((item, index) => (
                    <tr key={item.orderItemId || index}>
                      <td className="admin-order-detail-product-name">
                        <div>{getProductName(item)}</div>
                        <div className="admin-order-detail-product-code">
                          Code: {getProductCode(item)}
                        </div>
                        {item.variantDetails &&
                          item.variantDetails.length > 0 && (
                            <div className="admin-order-detail-variant-details">
                              <ul className="admin-order-detail-variant-list">
                                {item.variantDetails.map((variant, idx) => (
                                  <li
                                    key={idx}
                                    className="admin-order-detail-variant-item"
                                  >
                                    <span className="admin-order-detail-variant-name">
                                      {variant.specificationName}:
                                    </span>
                                    <span className="admin-order-detail-variant-value">
                                      {variant.specificationValue}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </td>
                      <td className="admin-order-detail-quantity">
                        {item.quantity}
                      </td>
                      <td className="admin-order-detail-price">
                        RM{parseFloat(item.price).toFixed(2)}
                      </td>
                      <td className="admin-order-detail-subtotal">
                        RM{calculateSubtotal(item.price, item.quantity)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center" }}>
                      No items found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="admin-order-detail-actions">
        <button
          className="admin-order-detail-btn-action admin-order-detail-btn-secondary"
          onClick={handlePrint}
        >
          <i className="fas fa-print"></i> Print Order
        </button>
      </div>
    </div>
  );
};

export default AdminOrderDetail;